import React, { useState } from 'react';
import { Download, Upload, Database, FileText, CheckCircle, AlertCircle, X, Trash2, ChevronRight, Languages, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  companiesAPI,
  customersAPI,
  registrarsAPI,
  serversAPI,
  domainsAPI,
  companyPaymentsAPI,
  domainPricesAPI,
  hostingPricesAPI,
  settingsAPI,
  accountsAPI,
  accountTransactionsAPI,
  usersAPI,
  paymentsAPI,
  mobileAccessKeysAPI,
  languagesAPI,
  translationsAPI
} from '../lib/api';
import { useTranslation, clearTranslationCache } from '../lib/i18n';
import apiClient from '../lib/apiClient';

const DataManagement = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('export');
  const [selectedTables, setSelectedTables] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importOptions, setImportOptions] = useState({
    clearExisting: false,
    skipDuplicates: true,
    updateExisting: false
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, currentTable: '', currentRecord: 0, totalRecords: 0 });
  const [deletingData, setDeletingData] = useState(false);
  const [loadingDemoData, setLoadingDemoData] = useState(false);
  const [demoProgress, setDemoProgress] = useState(0);

  const categories = [
    { id: 'export', name: 'Veri Dışa Aktar', icon: Download },
    { id: 'import', name: 'Veri İçe Aktar', icon: Upload },
    { id: 'advanced', name: 'Gelişmiş İşlemler', icon: Database }
  ];

  // Tabloları bağımlılık sırasına göre sırala (önce bağımsızlar, sonra bağımlılar)
  const availableTables = [
    // Bağımsız tablolar (foreign key gerektirmeyen)
    { id: 'companies', name: t('dataManagement.tables.companies'), description: t('dataManagement.tables.companiesDesc'), api: companiesAPI, matchField: 'name' },
    { id: 'registrars', name: t('dataManagement.tables.registrars'), description: t('dataManagement.tables.registrarsDesc'), api: registrarsAPI, matchField: 'name' },
    { id: 'servers', name: t('dataManagement.tables.servers'), description: t('dataManagement.tables.serversDesc'), api: serversAPI, matchField: 'name' },
    { id: 'accounts', name: t('dataManagement.tables.accounts'), description: t('dataManagement.tables.accountsDesc'), api: accountsAPI, matchField: 'name' },
    { id: 'languages', name: t('dataManagement.tables.languages'), description: t('dataManagement.tables.languagesDesc'), api: languagesAPI, matchField: 'code' },
    { id: 'users', name: t('dataManagement.tables.users'), description: t('dataManagement.tables.usersDesc'), api: usersAPI, matchField: 'email' },
    { id: 'domain_prices', name: t('dataManagement.tables.domainPrices'), description: t('dataManagement.tables.domainPricesDesc'), api: domainPricesAPI, matchField: 'extension' },
    { id: 'hosting_prices', name: t('dataManagement.tables.hostingPrices'), description: t('dataManagement.tables.hostingPricesDesc'), api: hostingPricesAPI, matchField: 'name' },
    { id: 'settings', name: t('dataManagement.tables.settings'), description: t('dataManagement.tables.settingsDesc'), api: settingsAPI, matchField: 'key' },
    
    // Bağımlı tablolar (foreign key gerektirenler)
    { id: 'customers', name: t('dataManagement.tables.customers'), description: t('dataManagement.tables.customersDesc'), api: customersAPI, matchField: 'email' },
    { id: 'domains', name: t('dataManagement.tables.domains'), description: t('dataManagement.tables.domainsDesc'), api: domainsAPI, matchField: 'domain_name' },
    { id: 'company_payments', name: t('dataManagement.tables.companyPayments'), description: t('dataManagement.tables.companyPaymentsDesc'), api: companyPaymentsAPI, matchField: null },
    { id: 'payments', name: t('dataManagement.tables.payments'), description: t('dataManagement.tables.paymentsDesc'), api: paymentsAPI, matchField: null },
    { id: 'account_transactions', name: t('dataManagement.tables.accountTransactions'), description: t('dataManagement.tables.accountTransactionsDesc'), api: accountTransactionsAPI, matchField: null },
    { id: 'translations', name: t('dataManagement.tables.translations'), description: t('dataManagement.tables.translationsDesc'), api: translationsAPI, matchField: null },
    { id: 'mobile_access_keys', name: t('dataManagement.tables.mobileAccessKeys'), description: t('dataManagement.tables.mobileAccessKeysDesc'), api: mobileAccessKeysAPI, matchField: 'access_key' }
  ];

  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (e) {
      console.error('User parse error:', e);
    }
    return null;
  };

  const handleDeleteAllData = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.id) {
      toast.error('Kullanıcı bulunamadı');
      return;
    }

    const confirmed = window.confirm('Tüm verileri silmek istediğinize emin misiniz? Bu işlem geri alınamaz!');

    if (!confirmed) {
      return;
    }

    setDeletingData(true);
    try {
      toast.loading('Veriler siliniyor...', { id: 'delete-data' });

      const response = await apiClient.delete('/data/delete-all', {
        data: { currentUserId: currentUser.id }
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast.success('Tüm veriler başarıyla silindi', {
        id: 'delete-data',
        duration: 5000
      });

      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Veri silme hatası:', error);
      toast.error('Veriler silinirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'), {
        id: 'delete-data',
        duration: 5000
      });
    } finally {
      setDeletingData(false);
    }
  };

  const handleLoadDemoData = async () => {
    const confirmed = window.confirm('Demo verilerini yüklemek istediğinize emin misiniz?');

    if (!confirmed) {
      return;
    }

    setLoadingDemoData(true);
    setDemoProgress(0);

    try {
      toast.loading('Demo veriler yükleniyor...', { id: 'load-demo' });

      const response = await apiClient.post('/data/load-demo', {});
      
      if (response.error) {
        throw new Error(response.error);
      }

      for (let i = 1; i <= 100; i += 10) {
        setDemoProgress(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      toast.success('Demo veriler başarıyla yüklendi', {
        id: 'load-demo',
        duration: 5000
      });

      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Demo veri yükleme hatası:', error);
      toast.error('Demo veriler yüklenemedi: ' + (error.message || 'Bilinmeyen hata'), {
        id: 'load-demo',
        duration: 5000
      });
    } finally {
      setLoadingDemoData(false);
      setDemoProgress(0);
    }
  };

  const handleTableToggle = (tableId) => {
    setSelectedTables(prev =>
      prev.includes(tableId)
        ? prev.filter(id => id !== tableId)
        : [...prev, tableId]
    );
  };

  const handleSelectAll = () => {
    setSelectedTables(availableTables.map(table => table.id));
  };

  const handleSelectNone = () => {
    setSelectedTables([]);
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      toast.loading(t('dataManagement.exportLoading'), { id: 'export' });

      // Tüm tabloları API'den çek
      const exportData = {};
      const statistics = {};

      for (const table of availableTables) {
        try {
          const data = await table.api.getAll();
          exportData[table.id] = Array.isArray(data) ? data : [];
          statistics[table.id] = exportData[table.id].length;
        } catch (error) {
          console.error(`❌ ${table.name} ${t('dataManagement.exportError')}:`, error);
          exportData[table.id] = [];
          statistics[table.id] = 0;
        }
      }

      // Export bilgileri
      const exportInfo = {
        export_date: new Date().toISOString(),
        export_version: '1.0',
        tables: availableTables.map(t => t.id)
      };

      const finalData = {
        export_info: exportInfo,
        data: exportData,
        statistics: statistics
      };

      // JSON dosyasını indir
      const dataStr = JSON.stringify(finalData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const linkElement = document.createElement('a');
      linkElement.href = url;
      linkElement.download = `domain_control_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);
      URL.revokeObjectURL(url);

      const totalRecords = Object.values(statistics).reduce((sum, count) => sum + count, 0);
      toast.success(`${t('dataManagement.exportSuccess')}: ${totalRecords} ${t('dataManagement.exportSuccessCount')}`, { id: 'export' });
    } catch (error) {
      console.error(t('dataManagement.exportError'), error);
      toast.error(`${t('dataManagement.exportError')}: ${error.message || t('dataManagement.unknownError')}`, { id: 'export' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSelected = async () => {
    if (selectedTables.length === 0) {
      toast.error(t('dataManagement.selectAtLeastOne'));
      return;
    }

    setIsExporting(true);
    try {
      toast.loading(t('dataManagement.exportSelectedLoading'), { id: 'export-selected' });

      // Seçili tabloları API'den çek
      const exportData = {};
      const statistics = {};

      for (const tableId of selectedTables) {
        const table = availableTables.find(t => t.id === tableId);
        if (!table) continue;

        try {
          const data = await table.api.getAll();
          exportData[tableId] = Array.isArray(data) ? data : [];
          statistics[tableId] = exportData[tableId].length;
        } catch (error) {
          console.error(`❌ ${table.name} ${t('dataManagement.exportError')}:`, error);
          exportData[tableId] = [];
          statistics[tableId] = 0;
        }
      }

      // Export bilgileri
      const exportInfo = {
        export_date: new Date().toISOString(),
        export_version: '1.0',
        tables: selectedTables
      };

      const finalData = {
        export_info: exportInfo,
        data: exportData,
        statistics: statistics
      };

      // JSON dosyasını indir
      const dataStr = JSON.stringify(finalData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const linkElement = document.createElement('a');
      linkElement.href = url;
      linkElement.download = `domain_control_export_${selectedTables.join('_')}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(linkElement);
      linkElement.click();
      document.body.removeChild(linkElement);
      URL.revokeObjectURL(url);

      const totalRecords = Object.values(statistics).reduce((sum, count) => sum + count, 0);
      toast.success(`${t('dataManagement.exportSelectedSuccess')}: ${totalRecords} ${t('dataManagement.exportSuccessCount')}`, { id: 'export-selected' });
    } catch (error) {
      console.error(t('dataManagement.exportError'), error);
      toast.error(`${t('dataManagement.exportError')}: ${error.message || t('dataManagement.unknownError')}`, { id: 'export-selected' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // JSON dosyası kontrolü
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setImportFile(file);
        setValidationResult(null);
      } else {
        toast.error(t('dataManagement.selectValidJson'));
      }
    }
  };

  const handleValidateFile = async () => {
    if (!importFile) {
      toast.error(t('dataManagement.selectFileFirst'));
      return;
    }

    try {
      toast.loading(t('dataManagement.validatingFile'), { id: 'validate' });

      const fileContent = await importFile.text();
      const importData = JSON.parse(fileContent);

      // Dosya formatını kontrol et
      if (!importData.data && !importData.export_info) {
        // Eski format - direkt tablo verileri
        const validationResult = {
          valid: true,
          tables: {}
        };

        // Her tabloyu kontrol et
        for (const table of availableTables) {
          if (importData[table.id] || importData.data?.[table.id]) {
            const data = importData[table.id] || importData.data[table.id];
            validationResult.tables[table.id] = {
              record_count: Array.isArray(data) ? data.length : 0,
              valid_records: Array.isArray(data) ? data.length : 0,
              invalid_records: 0
            };
          }
        }

        setValidationResult(validationResult);
        toast.success(t('dataManagement.validationSuccess'), { id: 'validate' });
      } else {
        // Yeni format - export_info ile
        const validationResult = {
          valid: true,
          tables: {}
        };

        const data = importData.data || importData;
        for (const table of availableTables) {
          if (data[table.id]) {
            const tableData = data[table.id];
            validationResult.tables[table.id] = {
              record_count: Array.isArray(tableData) ? tableData.length : 0,
              valid_records: Array.isArray(tableData) ? tableData.length : 0,
              invalid_records: 0
            };
          }
        }

        setValidationResult(validationResult);
        toast.success(t('dataManagement.validationSuccess'), { id: 'validate' });
      }
    } catch (error) {
      console.error(t('dataManagement.validationError'), error);
      setValidationResult({
        valid: false,
        tables: {},
        error: error.message
      });
      toast.error(`${t('dataManagement.validationError')}: ${error.message}`, { id: 'validate' });
    }
  };

  // Helper: İsim/unique field'a göre kayıt bul
  const findRecordByField = async (table, matchField, value) => {
    if (!matchField || !value) return null;

    try {
      // API ile tüm kayıtları al ve filtrele
      const allData = await table.api.getAll();
      if (!Array.isArray(allData)) return null;

      return allData.find(record => record[matchField] === value) || null;
    } catch (e) {
      return null;
    }
  };

  // Helper: CamelCase'den snake_case'a alan adı dönüşümü
  const camelToSnakeCase = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    
    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        // CamelCase'den snake_case'a dönüştür
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        result[snakeKey] = obj[key];
      }
    }
    return result;
  };

  // Helper: Boolean değerini normalize et (PostgreSQL 't'/'f' -> true/false)
  const normalizeBoolean = (value) => {
    if (value === true || value === 'true' || value === 't' || value === 1 || value === '1') {
      return true;
    }
    if (value === false || value === 'false' || value === 'f' || value === 0 || value === '0') {
      return false;
    }
    return value;
  };

  // Helper: Date değerini normalize et
  const normalizeDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Helper: Record'u temizle ve alan eşleştirmelerini yap
  const cleanAndMapRecord = (record, tableId, data, foreignKeyMaps) => {
    // customers için companies array'ini ÖNCE işle (snake_case dönüştürmeden)
    let companyIds = [];
    if (tableId === 'customers' && record.companies && Array.isArray(record.companies)) {
      console.log('DEBUG: Processing companies for customer:', record.email, 'companies:', record.companies);
      console.log('DEBUG: foreignKeyMaps.companies:', foreignKeyMaps.companies);
      for (const company of record.companies) {
        const companyName = company.name;
        console.log('DEBUG: Looking for company:', companyName, 'found:', foreignKeyMaps.companies?.[companyName]);
        if (companyName && foreignKeyMaps.companies?.[companyName]) {
          companyIds.push(foreignKeyMaps.companies[companyName]);
        }
      }
      console.log('DEBUG: Final companyIds:', companyIds);
    }

    // Önce camelCase'den snake_case'a dönüştür
    let cleanData = camelToSnakeCase(record);
    
    // id'yi ve otomatik alanları kaldır
    const { id, created_at, updated_at, ...restData } = cleanData;
    cleanData = restData;

    // Tüm boolean alanları normalize et
    for (const key in cleanData) {
      if (key === 'is_active' || key === 'isActive' || key === 'auto_renew' || key === 'autoRenew') {
        cleanData[key] = normalizeBoolean(cleanData[key]);
      }
    }

    // Servers için hostname -> ipAddress dönüşümü
    if (tableId === 'servers' && cleanData.hostname) {
      cleanData.ip_address = cleanData.hostname;
      delete cleanData.hostname;
    }

    // Settings için setting_key -> key, setting_value -> value dönüşümü
    if (tableId === 'settings') {
      if (cleanData.setting_key) {
        cleanData.key = cleanData.setting_key;
        delete cleanData.setting_key;
      }
      if (cleanData.setting_value) {
        cleanData.value = cleanData.setting_value;
        delete cleanData.setting_value;
      }
    }

    // CompanyPayments için tüm alanları normalize et
    if (tableId === 'company_payments') {
      // Amount fields normalization
      if (cleanData.amount !== undefined) {
        cleanData.amount = parseFloat(cleanData.amount) || 0;
      }
      if (cleanData.purchase_price !== undefined && cleanData.purchase_price !== null && cleanData.purchase_price !== '') {
        cleanData.purchase_price = parseFloat(cleanData.purchase_price) || 0;
      }
      if (cleanData.sale_price !== undefined && cleanData.sale_price !== null && cleanData.sale_price !== '') {
        cleanData.sale_price = parseFloat(cleanData.sale_price) || 0;
      }
      
      // Payment probability normalization (0-100)
      if (cleanData.payment_probability !== undefined && cleanData.payment_probability !== null && cleanData.payment_probability !== '') {
        cleanData.payment_probability = Math.min(100, Math.max(0, parseInt(cleanData.payment_probability) || 100));
      }
      
      // Boolean fields normalization
      if (cleanData.is_active !== undefined) {
        cleanData.is_active = normalizeBoolean(cleanData.is_active);
      }
      if (cleanData.auto_renew !== undefined) {
        cleanData.auto_renew = normalizeBoolean(cleanData.auto_renew);
      }
      
      // Date fields normalization
      if (cleanData.start_date) {
        cleanData.start_date = normalizeDate(cleanData.start_date);
      }
      if (cleanData.end_date) {
        cleanData.end_date = normalizeDate(cleanData.end_date);
      }
      if (cleanData.next_payment_date) {
        cleanData.next_payment_date = normalizeDate(cleanData.next_payment_date);
      }
      
      // Handle company_id mapping - required field, must be valid
      if (cleanData.company_id) {
        const companyName = data.companies?.find(c => c.id === cleanData.company_id)?.name;
        if (companyName && foreignKeyMaps.companies?.[companyName]) {
          cleanData.company_id = foreignKeyMaps.companies[companyName];
        } else {
          // Try to find company by name directly in the import data
          const companyInImport = data.companies?.find(c => c.name === companyName);
          if (companyInImport && foreignKeyMaps.companies?.[companyInImport.name]) {
            cleanData.company_id = foreignKeyMaps.companies[companyInImport.name];
          } else {
            // Mark record as invalid - company_id is required
            cleanData._skip = true;
            cleanData._skipReason = 'Invalid company_id';
          }
        }
      } else {
        // company_id is required
        cleanData._skip = true;
        cleanData._skipReason = 'Missing company_id';
      }
      
      // Handle customer_id mapping
      if (cleanData.customer_id) {
        const customerEmail = data.customers?.find(c => c.id === cleanData.customer_id)?.email;
        if (customerEmail && foreignKeyMaps.customers?.[customerEmail]) {
          cleanData.customer_id = foreignKeyMaps.customers[customerEmail];
        } else {
          delete cleanData.customer_id;
        }
      }
      
      // Handle domain_id mapping
      if (cleanData.domain_id) {
        const domainName = data.domains?.find(d => d.id === cleanData.domain_id)?.domain_name;
        if (domainName && foreignKeyMaps.domains?.[domainName]) {
          cleanData.domain_id = foreignKeyMaps.domains[domainName];
        } else {
          delete cleanData.domain_id;
        }
      }
    }

    // DomainPrices için price fields'ı numeric olarak dönüştür
    if (tableId === 'domain_prices') {
      if (cleanData.purchase_price !== undefined) {
        cleanData.purchase_price = parseFloat(cleanData.purchase_price) || 0;
      }
      if (cleanData.sale_price !== undefined) {
        cleanData.sale_price = parseFloat(cleanData.sale_price) || 0;
      }
      if (cleanData.tld) {
        cleanData.extension = cleanData.tld;
        delete cleanData.tld;
      }
    }

    // HostingPrices için price fields'ı numeric olarak dönüştür
    if (tableId === 'hosting_prices') {
      if (cleanData.purchase_price !== undefined) {
        cleanData.purchase_price = parseFloat(cleanData.purchase_price) || 0;
      }
      if (cleanData.sale_price !== undefined) {
        cleanData.sale_price = parseFloat(cleanData.sale_price) || 0;
      }
    }

    // Foreign key'leri isimlere göre eşleştir
    if (tableId === 'customers') {
      // companies array'ini sil (snake_case dönüşümünden sonra kalabilir)
      delete cleanData.companies;

      // company_id field'ını işle
      if (cleanData.company_id) {
        const companyName = data.companies?.find(c => c.id === cleanData.company_id)?.name;
        if (companyName && foreignKeyMaps.companies?.[companyName]) {
          cleanData.company_id = foreignKeyMaps.companies[companyName];
        } else {
          delete cleanData.company_id;
        }
      }

      // ÖNCE işlediğimiz companies array'den elde edilen company_ids'i kullan
      if (companyIds.length > 0) {
        cleanData.company_ids = companyIds;
      } else {
        // Mevcut company_ids array'ini de eşleştir (varsa)
        if (cleanData.company_ids && Array.isArray(cleanData.company_ids)) {
          cleanData.company_ids = cleanData.company_ids
            .map(oldId => {
              const companyName = data.companies?.find(c => c.id === oldId)?.name;
              if (companyName && foreignKeyMaps.companies?.[companyName]) {
                return foreignKeyMaps.companies[companyName];
              }
              return null;
            })
            .filter(Boolean);
        }
      }
    }

    if (tableId === 'domains') {
      // Extract extension from domain_name if not provided
      if (cleanData.domain_name && !cleanData.extension) {
        const parts = cleanData.domain_name.split('.');
        if (parts.length >= 2) {
          cleanData.extension = parts[parts.length - 1];
        }
      }
      
      // Date fields normalization
      if (cleanData.registration_date) {
        cleanData.registration_date = normalizeDate(cleanData.registration_date);
      }
      if (cleanData.expiration_date) {
        cleanData.expiration_date = normalizeDate(cleanData.expiration_date);
      }
      if (cleanData.auto_renew_date) {
        cleanData.auto_renew_date = normalizeDate(cleanData.auto_renew_date);
      }
      
      if (cleanData.company_id) {
        const companyName = data.companies?.find(c => c.id === cleanData.company_id)?.name;
        if (companyName && foreignKeyMaps.companies?.[companyName]) {
          cleanData.company_id = foreignKeyMaps.companies[companyName];
        } else {
          delete cleanData.company_id;
        }
      }
      if (cleanData.customer_id) {
        const customerEmail = data.customers?.find(c => c.id === cleanData.customer_id)?.email;
        if (customerEmail && foreignKeyMaps.customers?.[customerEmail]) {
          cleanData.customer_id = foreignKeyMaps.customers[customerEmail];
        } else {
          delete cleanData.customer_id;
        }
      }
      if (cleanData.registrar_id) {
        const registrarName = data.registrars?.find(r => r.id === cleanData.registrar_id)?.name;
        if (registrarName && foreignKeyMaps.registrars?.[registrarName]) {
          cleanData.registrar_id = foreignKeyMaps.registrars[registrarName];
        } else {
          delete cleanData.registrar_id;
        }
      }
      if (cleanData.server_id) {
        const serverName = data.servers?.find(s => s.id === cleanData.server_id)?.name;
        if (serverName && foreignKeyMaps.servers?.[serverName]) {
          cleanData.server_id = foreignKeyMaps.servers[serverName];
        } else {
          delete cleanData.server_id;
        }
      }
    }

    // Account Transactions için account_id mapping
    if (tableId === 'account_transactions') {
      if (cleanData.amount !== undefined) {
        cleanData.amount = parseFloat(cleanData.amount) || 0;
      }
      
      // Date fields normalization
      if (cleanData.transaction_date) {
        cleanData.transaction_date = normalizeDate(cleanData.transaction_date);
      }
      
      // Handle account_id mapping
      if (cleanData.account_id) {
        const accountName = data.accounts?.find(a => a.id === cleanData.account_id)?.name;
        if (accountName && foreignKeyMaps.accounts?.[accountName]) {
          cleanData.account_id = foreignKeyMaps.accounts[accountName];
        } else {
          cleanData._skip = true;
          cleanData._skipReason = 'Invalid account_id';
        }
      } else {
        cleanData._skip = true;
        cleanData._skipReason = 'Missing account_id';
      }
    }

    // Payments için normalizations
    if (tableId === 'payments') {
      if (cleanData.amount !== undefined) {
        cleanData.amount = parseFloat(cleanData.amount) || 0;
      }
      
      // Date fields normalization
      if (cleanData.payment_date) {
        cleanData.payment_date = normalizeDate(cleanData.payment_date);
      }
      if (cleanData.due_date) {
        cleanData.due_date = normalizeDate(cleanData.due_date);
      }
      
      // Handle company_id mapping
      if (cleanData.company_id) {
        const companyName = data.companies?.find(c => c.id === cleanData.company_id)?.name;
        if (companyName && foreignKeyMaps.companies?.[companyName]) {
          cleanData.company_id = foreignKeyMaps.companies[companyName];
        } else {
          delete cleanData.company_id;
        }
      }
      
      // Handle customer_id mapping
      if (cleanData.customer_id) {
        const customerEmail = data.customers?.find(c => c.id === cleanData.customer_id)?.email;
        if (customerEmail && foreignKeyMaps.customers?.[customerEmail]) {
          cleanData.customer_id = foreignKeyMaps.customers[customerEmail];
        } else {
          delete cleanData.customer_id;
        }
      }
    }

    // Translations için language_id mapping
    if (tableId === 'translations') {
      if (cleanData.language_id) {
        const languageCode = data.languages?.find(l => l.id === cleanData.language_id)?.code;
        if (languageCode && foreignKeyMaps.languages?.[languageCode]) {
          cleanData.language_id = foreignKeyMaps.languages[languageCode];
        } else {
          cleanData._skip = true;
          cleanData._skipReason = 'Invalid language_id';
        }
      }
    }

    // Mobile Access Keys için user_id mapping
    if (tableId === 'mobile_access_keys') {
      if (cleanData.user_id) {
        const userEmail = data.users?.find(u => u.id === cleanData.user_id)?.email;
        if (userEmail && foreignKeyMaps.users?.[userEmail]) {
          cleanData.user_id = foreignKeyMaps.users[userEmail];
        } else {
          delete cleanData.user_id;
        }
      }
      
      // Date fields normalization
      if (cleanData.expires_at) {
        cleanData.expires_at = normalizeDate(cleanData.expires_at);
      }
      if (cleanData.last_used_at) {
        cleanData.last_used_at = normalizeDate(cleanData.last_used_at);
      }
    }

    // Accounts için balance normalization
    if (tableId === 'accounts') {
      if (cleanData.balance !== undefined) {
        cleanData.balance = parseFloat(cleanData.balance) || 0;
      }
      if (cleanData.is_active !== undefined) {
        cleanData.is_active = normalizeBoolean(cleanData.is_active);
      }
    }

    // Users için password handling
    if (tableId === 'users') {
      // Password'u export etmiyoruz güvenlik nedeniyle, import sırasında atla
      delete cleanData.password;
      delete cleanData.password_hash;
    }

    return cleanData;
  };

  const handleImportFile = async () => {
    if (!importFile) {
      toast.error(t('dataManagement.selectFileFirst'));
      return;
    }

    if (!validationResult || !validationResult.valid) {
      toast.error(t('dataManagement.validateFirst'));
      return;
    }

    setIsImporting(true);
    try {
      toast.loading(t('dataManagement.importLoading'), { id: 'import' });

      const fileContent = await importFile.text();
      const importData = JSON.parse(fileContent);

      // Veri formatını belirle
      const data = importData.data || importData;
      const statistics = { total_imported: 0, total_updated: 0, total_skipped: 0 };

      // Toplam kayıt sayısını hesapla
      let totalRecords = 0;
      for (const table of availableTables) {
        const tableData = data[table.id];
        if (tableData && Array.isArray(tableData)) {
          totalRecords += tableData.length;
        }
      }

      setImportProgress({ current: 0, total: availableTables.length, currentTable: '', currentRecord: 0, totalRecords });

      // Foreign key mapping'leri oluştur (isim -> ID)
      const foreignKeyMaps = {
        companies: {},
        registrars: {},
        servers: {},
        customers: {},
        domains: {},
        settings: {},
        accounts: {},
        languages: {},
        users: {},
        domain_prices: {},
        hosting_prices: {},
        payments: {},
        account_transactions: {},
        translations: {},
        mobile_access_keys: {}
      };

      // Önce tüm bağımsız tabloları import et ve mapping'leri oluştur
      const independentTables = ['companies', 'registrars', 'servers', 'accounts', 'languages', 'users', 'domain_prices', 'hosting_prices', 'settings'];
      for (const tableId of independentTables) {
        const table = availableTables.find(t => t.id === tableId);
        if (!table) continue;

        const tableData = data[tableId];
        if (!tableData || !Array.isArray(tableData) || tableData.length === 0) continue;

        // YENİ: Import etmeden önce mevcut recordları mapping'e ekle
        for (const record of tableData) {
          let matchValue = null;
          if (tableId === 'settings') {
            matchValue = record.key || record.setting_key;
          } else if (table.matchField) {
            matchValue = record[table.matchField];
          }

          if (matchValue) {
            const existingRecord = await findRecordByField(table, table.matchField, matchValue);
            if (existingRecord) {
              foreignKeyMaps[tableId][matchValue] = existingRecord.id;
            }
          }
        }

        // YENİ: Bağımsız tabloları import et ve yeni mapping'leri ekle
        for (const record of tableData) {
          try {
            let matchValue = null;
            if (tableId === 'settings') {
              matchValue = record.key || record.setting_key;
            } else if (table.matchField) {
              matchValue = record[table.matchField];
            }

            // Mevcut recordu bul
            let existingRecord = null;
            if (matchValue) {
              existingRecord = await findRecordByField(table, table.matchField, matchValue);
            }

            // Eğer zaten mapping'de varsa, atla
            if (matchValue && foreignKeyMaps[tableId][matchValue]) {
              continue;
            }

            const cleanData = cleanAndMapRecord(record, tableId, data, foreignKeyMaps);

            if (cleanData._skip) {
              continue;
            }

            const { _skip, _skipReason, ...dataToImport } = cleanData;

            if (!existingRecord) {
              // Yeni kayıt ekle
              try {
                const result = await table.api.create(dataToImport);

                // Mapping'e ekle
                if (result) {
                  if (tableId === 'settings' && result.key) {
                    foreignKeyMaps.settings[result.key] = result.id;
                  } else if (table.matchField && result[table.matchField]) {
                    if (!foreignKeyMaps[tableId]) {
                      foreignKeyMaps[tableId] = {};
                    }
                    foreignKeyMaps[tableId][result[table.matchField]] = result.id;
                  }
                }
              } catch (createError) {
                console.error(`Create ${t('dataManagement.importError')} (${table.name}):`, createError);
              }
            }
          } catch (error) {
            console.error(`Error processing ${tableId} record:`, error);
          }
        }
      }

      // Her tablo için import işlemi
      let tableIndex = 0;
      let processedRecords = 0;
      for (const table of availableTables) {
        // Bağımsız tablolar zaten import edildi, atla
        if (independentTables.includes(table.id)) {
          tableIndex++;
          continue;
        }

        const tableData = data[table.id];
        if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
          tableIndex++;
          continue;
        }

        const currentTableIndex = tableIndex;
        setImportProgress(prev => ({ ...prev, current: currentTableIndex, currentTable: table.name }));

        try {
          let imported = 0;
          let skipped = 0;

          // Customers ve domains için mapping'leri güncelle
          if (table.id === 'customers') {
            const customersData = data.customers;
            if (customersData && Array.isArray(customersData)) {
              for (const customer of customersData) {
                if (customer.email) {
                  const existingCustomer = await findRecordByField(table, 'email', customer.email);
                  if (existingCustomer) {
                    foreignKeyMaps.customers[customer.email] = existingCustomer.id;
                  }
                }
              }
            }
          }

          if (table.id === 'domains') {
            const domainsData = data.domains;
            if (domainsData && Array.isArray(domainsData)) {
              for (const domain of domainsData) {
                if (domain.domain_name) {
                  const existingDomain = await findRecordByField(table, 'domain_name', domain.domain_name);
                  if (existingDomain) {
                    foreignKeyMaps.domains[domain.domain_name] = existingDomain.id;
                  }
                }
              }
            }
          }

          for (const record of tableData) {
            processedRecords++;
            const currentProcessedRecords = processedRecords;
            setImportProgress(prev => ({ ...prev, currentRecord: currentProcessedRecords }));
            try {
              // İsim/unique field'a göre mevcut kaydı bul
              let existingRecord = null;
              let matchValue = null;

              if (table.id === 'settings') {
                matchValue = record.key || record.setting_key;
                if (matchValue) {
                  existingRecord = await findRecordByField(table, 'key', matchValue);
                }
              } else if (table.matchField && record[table.matchField]) {
                matchValue = record[table.matchField];
                existingRecord = await findRecordByField(table, table.matchField, matchValue);
              }
              // Don't try to find by old ID - just create new records
              // This prevents "404 not found" errors for records that don't exist

              if (importOptions.clearExisting && existingRecord) {
                // Mevcut kaydı sil
                try {
                  await table.api.delete(existingRecord.id);
                  existingRecord = null;
                } catch (e) {
                  // Kayıt yoksa hata vermez
                }
              }

              const cleanData = cleanAndMapRecord(record, table.id, data, foreignKeyMaps);

              // Skip records marked as invalid (missing required foreign keys)
              if (cleanData._skip) {
                console.warn(`Skipping ${table.name} record: ${cleanData._skipReason || 'Invalid data'}`);
                skipped++;
                continue;
              }

              // Remove internal skip flags
              const { _skip, _skipReason, ...dataToImport } = cleanData;

              if (importOptions.updateExisting && existingRecord) {
                // Mevcut kaydı güncelle
                try {
                  // Boş obje kontrolü
                  if (Object.keys(dataToImport).length === 0) {
                    skipped++;
                    continue;
                  }

                  await table.api.update(existingRecord.id, dataToImport);
                  imported++;
                } catch (e) {
                  console.error(`Update ${t('dataManagement.importError')} (${table.name}):`, e);
                  skipped++;
                }
              } else if (importOptions.skipDuplicates && existingRecord) {
                // Duplicate kayıt var, atla
                skipped++;
                continue;
              } else {
                // Yeni kayıt ekle
                try {
                  console.log(`DEBUG: Creating ${table.id} with data:`, dataToImport);
                  const result = await table.api.create(dataToImport);
                  console.log(`DEBUG: Created ${table.id}, result:`, result);

                  // Mapping'e ekle (yeni oluşturulan kayıt için)
                  if (result) {
                    if (table.id === 'settings' && result.key) {
                      foreignKeyMaps.settings[result.key] = result.id;
                    } else if (table.matchField && result[table.matchField]) {
                      // Ensure the map exists for this table
                      if (!foreignKeyMaps[table.id]) {
                        foreignKeyMaps[table.id] = {};
                      }
                      foreignKeyMaps[table.id][result[table.matchField]] = result.id;
                    }
                  }

                  imported++;
                } catch (createError) {
                  console.error(`Create ${t('dataManagement.importError')} (${table.name}):`, createError);
                  skipped++;
                }
              }
            } catch (error) {
              console.error(`❌ ${table.name} ${t('dataManagement.importError')}:`, error);
              skipped++;
            }
          }

          statistics.total_imported += imported;
          statistics.total_skipped += skipped;

          tableIndex++;
        } catch (error) {
          console.error(`❌ ${table.name} ${t('dataManagement.importError')}:`, error);
          tableIndex++;
        }
      }

      setImportProgress({ current: 0, total: 0, currentTable: '', currentRecord: 0, totalRecords: 0 });

      toast.success(
        `${t('dataManagement.importSuccess')}: ${statistics.total_imported} ${t('dataManagement.importAdded')}, ${statistics.total_updated} ${t('dataManagement.importUpdated')}, ${statistics.total_skipped} ${t('dataManagement.importSkipped')}`,
        { id: 'import', duration: 5000 }
      );

      // Formu temizle
      setImportFile(null);
      setValidationResult(null);
      const fileInput = document.getElementById('import-file');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error(t('dataManagement.importError'), error);
      toast.error(`${t('dataManagement.importError')}: ${error.message || t('dataManagement.unknownError')}`, { id: 'import' });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-2 md:p-3">
      {/* Header */}
      <div className="mb-3 md:mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Veri Yönetimi</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 md:mt-2 text-sm md:text-base">Verilerinizi dışa/ice aktarın ve yönetin</p>
      </div>

      {/* Main Layout with Sidebar */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-4">
        {/* Sidebar */}
        <div className="w-full md:w-48 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden sticky top-24">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {categories.map((category) => {
                const Icon = category.icon;
                const isActive = activeCategory === category.id;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                    {isActive && <ChevronRight className="h-4 w-4" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-5">
            {activeCategory === 'export' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Veri Dışa Aktar</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Tüm Verileri Export */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Tüm Verileri Dışa Aktar</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                      Tüm tablolardaki verileri tek bir JSON dosyası olarak indirin
                    </p>
                    <button
                      onClick={handleExportAll}
                      disabled={isExporting}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {isExporting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Dışa Aktarılıyor...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          Tümünü İndir
                        </>
                      )}
                    </button>
                  </div>

                  {/* Seçili Tabloları Export */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Seçili Tabloları Dışa Aktar</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                      Sadece seçtiğiniz tabloları dışa aktarın
                    </p>

                    <div className="space-y-2 mb-4">
                      <div className="flex gap-2">
                        <button
                          onClick={handleSelectAll}
                          className="text-xs bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded"
                        >
                          Tümünü Seç
                        </button>
                        <button
                          onClick={handleSelectNone}
                          className="text-xs bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 px-2 py-1 rounded"
                        >
                          Seçimi Kaldır
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                        {availableTables.map(table => (
                          <label key={table.id} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={selectedTables.includes(table.id)}
                              onChange={() => handleTableToggle(table.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-gray-700"
                            />
                            <span className="text-gray-700 dark:text-gray-300">{table.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={handleExportSelected}
                      disabled={isExporting || selectedTables.length === 0}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {isExporting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Dışa Aktarılıyor...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          Seçilenleri İndir
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeCategory === 'import' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Veri İçe Aktar</h2>
                
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-orange-900 dark:text-orange-200">Uyarı</h4>
                      <p className="text-orange-800 dark:text-orange-300 text-sm mt-1">
                        İçe aktarma işlemi mevcut verileri etkileyebilir. Lütfen önce yedek alın.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Dosya Seçimi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      JSON Dosyası Seçin
                    </label>
                    <input
                      id="import-file"
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50"
                    />
                    {importFile && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Seçilen dosya: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>

                  {/* Import Seçenekleri */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">İçe Aktarma Seçenekleri</h4>
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={importOptions.clearExisting}
                          onChange={(e) => setImportOptions(prev => ({ ...prev, clearExisting: e.target.checked }))}
                          className="rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 bg-white dark:bg-gray-700"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Mevcut Verileri Temizle</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">İçe aktarmadan önce mevcut verileri siler</p>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={importOptions.skipDuplicates}
                          onChange={(e) => setImportOptions(prev => ({ ...prev, skipDuplicates: e.target.checked }))}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 bg-white dark:bg-gray-700"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tekrarları Atla</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Mevcut kayıtları atlayarak sadece yeni kayıtları ekler</p>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={importOptions.updateExisting}
                          onChange={(e) => setImportOptions(prev => ({ ...prev, updateExisting: e.target.checked }))}
                          className="rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 bg-white dark:bg-gray-700"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Mevcutları Güncelle</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Mevcut kayıtları yeni verilerle günceller</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Doğrulama Sonucu */}
                  {validationResult && (
                    <div className={`rounded-lg p-4 ${validationResult.valid ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                      <div className="flex items-start gap-3">
                        {validationResult.valid ? (
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                        ) : (
                          <X className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                        )}
                        <div>
                          <h4 className={`font-medium ${validationResult.valid ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>
                            {validationResult.valid ? 'Dosya Geçerli' : 'Dosyada Hatalar Var'}
                          </h4>
                          <div className="text-sm mt-2 space-y-1">
                            {Object.entries(validationResult.tables).map(([table, info]) => (
                              <div key={table} className="flex justify-between">
                                <span className="text-gray-700 dark:text-gray-300">{availableTables.find(t => t.id === table)?.name || table}</span>
                                <span className={`font-medium ${info.invalid_records > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                  {info.valid_records}/{info.record_count} geçerli
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Progress Göstergesi */}
                  {isImporting && importProgress.totalRecords > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
                            {importProgress.currentTable || 'Hazırlanıyor...'}
                          </span>
                          <span className="text-sm text-blue-700 dark:text-blue-300">
                            {importProgress.current > 0 && importProgress.total > 0
                              ? `${importProgress.current}/${importProgress.total} tablo`
                              : ''}
                          </span>
                        </div>
                        <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2.5">
                          <div
                            className="bg-blue-600 dark:bg-blue-400 h-2.5 rounded-full transition-all duration-300"
                            style={{
                              width: `${importProgress.totalRecords > 0
                                ? (importProgress.currentRecord / importProgress.totalRecords) * 100
                                : 0}%`
                            }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-blue-700 dark:text-blue-300">
                          <span>
                            {importProgress.currentRecord > 0 && importProgress.totalRecords > 0
                              ? `${importProgress.currentRecord}/${importProgress.totalRecords} kayıt işlendi`
                              : 'Hazırlanıyor...'}
                          </span>
                          <span>
                            {importProgress.totalRecords > 0
                              ? `%${Math.round((importProgress.currentRecord / importProgress.totalRecords) * 100)}`
                              : '%0'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Butonlar */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleValidateFile}
                      disabled={!importFile || isImporting}
                      className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Doğrula
                    </button>

                    <button
                      onClick={handleImportFile}
                      disabled={!importFile || !validationResult?.valid || isImporting}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      {isImporting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          İçe Aktarılıyor...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          İçe Aktar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeCategory === 'advanced' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Gelişmiş İşlemler</h2>
                
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm text-red-800 dark:text-red-200">
                      <p className="font-medium mb-1">⚠️ Dikkat</p>
                      <p className="text-xs">Bu bölümdeki işlemler geri alınamaz. Lütfen dikkatli kullanın.</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <Languages className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Çeviri Cache'ini Temizle</h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Tarayıcıda saklanan çeviri verilerini temizle
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('Çeviri cache\'i temizlemek istediğinize emin misiniz?')) {
                        const success = clearTranslationCache();
                        if (success) {
                          toast.success('Cache temizlendi');
                          setTimeout(() => window.location.reload(), 1000);
                        } else {
                          toast.error('Cache temizlenemedi');
                        }
                      }
                    }}
                    className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Temizle</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleDeleteAllData}
                  disabled={deletingData}
                  className="w-full flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {deletingData ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Siliniyor...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Tüm Verileri Sil
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleLoadDemoData}
                  disabled={loadingDemoData}
                  className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loadingDemoData ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4 mr-2" />
                      Demo Verileri Yükle
                    </>
                  )}
                </button>

                {demoProgress > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span>İlerleme</span>
                      <span>{Math.round(demoProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${demoProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataManagement;
